import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';

const OPTIONS = {
  types: ['(regions)'],
  componentRestrictions: {
    country: ['gb', 'uk'],
  },
};

export const validateGooglePlacesApi = () => {
  if (!window.google) {
    throw new Error(
      'Google Maps JavaScript API library must be loaded. See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library'
    );
  }

  if (!window.google.maps.places) {
    throw new Error(
      'Google Maps Places library must be loaded. Please add `libraries=places` to the src URL. See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library'
    );
  }
};

export default function useAddressPredictions(
  input,
  options = OPTIONS,
  getPlaces = false
) {
  const isCancelled = useRef();
  const { places } = window.google.maps;
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);

  const autocomplete = useRef();
  const geocoder = useRef();

  if (!autocomplete.current) {
    validateGooglePlacesApi();
    autocomplete.current = new places.AutocompleteService();
  }

  if (!geocoder.current) {
    geocoder.current = new window.google.maps.Geocoder();
  }

  if (!isCancelled) {
    isCancelled.current = false;
  }

  function getPlacePredictions(value) {
    setLoading(true);

    if (!value) {
      setPredictions([]);
      setLoading(false);
      return;
    }

    if (isCancelled.current) return;
    autocomplete.current.getPlacePredictions(
      {
        input: value,
        ...{ options },
      },
      results => {
        const nPredictions = results || [];
        if (!getPlaces) {
          setLoading(false);
          setPredictions(nPredictions);
        } else {
          const [best] = nPredictions;
          const { place_id: placeId } = best || {};
          if (!placeId) {
            setLoading(false);
            setPredictions([]);
          } else {
            if (isCancelled.current) return;
            geocoder.current.geocode({ placeId }, (res, status) => {
              setLoading(false);
              if (status === 'OK') {
                const place = res[0];
                setPredictions([place]);
              } else {
                setError(res);
                setPredictions([]);
              }
            });
          }
        }
      }
    );
  }

  const debouncedGetPlacePredictions = useCallback(
    debounce(getPlacePredictions, 500),
    []
  );

  useEffect(() => {
    debouncedGetPlacePredictions(input);
    // TODO: See a better way to handle this, as the field get's dismounted each time a character is entered
    // it get's cancelled before it needs to
    // return () => {
    //   isCancelled.current = true;
    // };
  }, [input, debouncedGetPlacePredictions]);

  return [predictions, loading, error];
}
